// Small shared status chips for the Cause & Effect UI.
//
// Colour is semantic and follows the fire-panel convention the spec leans on: amber =
// supervisory, red = live control, green = normal/confirmed, grey = inactive. It is deliberately
// NOT the page accent — a badge's colour has to mean something on its own.

import React from "react";
import { ShieldCheck, Zap, Eye, CircleDot, PauseCircle, PlayCircle, FlaskConical } from "lucide-react";
import { SAFETY_CLASSES, MODES } from "../../lib/automationModel";

const TONES = {
  amber:   "bg-amber-50 border-amber-200 text-amber-700",
  red:     "bg-red-50 border-red-200 text-red-700",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  sky:     "bg-sky-50 border-sky-200 text-sky-700",
  gray:    "bg-gray-100 border-gray-200 text-gray-600",
};

export function Chip({ tone = "gray", icon: Icon, children, title, className = "" }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold whitespace-nowrap ${TONES[tone] || TONES.gray} ${className}`}
    >
      {Icon && <Icon className="w-3 h-3" strokeWidth={2.5} />}
      {children}
    </span>
  );
}

const SAFETY_ICONS = {
  supervisory: Eye,
  control: Zap,
  control_backup: ShieldCheck,
};

export function SafetyClassBadge({ value }) {
  const def = SAFETY_CLASSES.find((s) => s.value === value) || SAFETY_CLASSES[0];
  return (
    <Chip tone={def.tone} icon={SAFETY_ICONS[def.value] || CircleDot} title={def.blurb}>
      {def.label}
    </Chip>
  );
}

const MODE_ICONS = {
  disabled: PauseCircle,
  simulate: FlaskConical,
  armed: PlayCircle,
};

export function ModeBadge({ value }) {
  const def = MODES.find((m) => m.value === value) || MODES[0];
  return (
    <Chip tone={def.tone} icon={MODE_ICONS[def.value] || CircleDot} title={def.blurb}>
      {def.label}
    </Chip>
  );
}

// Shown when a rule's circuit breaker has tripped (spec §7 rail 6) — it stops evaluating and
// needs a human to clear it, so this must never look like a passive status.
export function BreakerBadge({ reason }) {
  return (
    <Chip tone="red" icon={Zap} title={reason || "Too many actuations in one hour — rule stopped."}>
      Breaker tripped
    </Chip>
  );
}
